import { useEffect, useState } from 'react';
import PrimaryButton from "../../components/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton";
import TextInput from "../../components/TextInput";
import InputLabel from "../../components/InputLabel";
import Swal from "sweetalert2";
import { Editor } from '@tinymce/tinymce-react';
import SeoSection from './SeoSection';

// Simple Toggle Switch component
function Toggle({ checked, onChange, id, label }) {
    return (
        <label htmlFor={id} className="flex items-center cursor-pointer select-none">
            <div className="relative">
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                    className="sr-only"
                />
                <div className={`block w-10 h-6 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-950' : 'bg-gray-300'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${checked ? 'translate-x-4' : ''}`}></div>
            </div>
            <span className="ml-3 text-sm text-gray-700">{label}</span>
        </label>
    );
}

export default function AddBiznisi({ business,onCancel }) {
    function toBool(val) {
        return val === true || val === 1 || val === '1';
    }
    const [processing, setProcessing] = useState(false);
    const [processingDraft, setProcessingDraft] = useState(false);
    const [processingDelete, setProcessingDelete] = useState(false);
    const [formData, setFormData] = useState({
        naziv_brenda: business?.naziv_brenda || '',
        sazetak: business?.sazetak || '',
        lokacija: business?.lokacija || '',
        o_poslovanju: business?.content || '',
        cijena: business?.cijena || '',
        prihod: business?.prihod || '',
        sde: business?.sde || '',
        faza_prodaje: business?.faza_prodaje || '',
        listing_id: business?.listing_id || '',
        udio_na_prodaju: business?.udio_na_prodaju || '',
        ekskluzivna_ponuda: business ? toBool(business.ekskluzivna_ponuda) : false,
        istaknuta_ponuda: business ? toBool(business.istaknuta_ponuda) : false,
        delisted: business ? toBool(business.delisted) : false,
        draft: business ? toBool(business.status === 'draft') : false,
        kategorija: business?.kategorija?.[0] || '',
        featured_image: null,
        notes: business?.notes,
    });

    const [categories, setCategories] = useState([]);

    // Yoast SEO state
    const existingSeo = business?.seo;
    const hasExistingCustomSeo = Boolean(
        (existingSeo?.title && existingSeo.title.trim()) || 
        (existingSeo?.metadesc && existingSeo.metadesc.trim()) ||
        (existingSeo?.focuskw && existingSeo.focuskw.trim())
    );

    const [customSeoEnabled, setCustomSeoEnabled] = useState(hasExistingCustomSeo);
    const [seoData, setSeoData] = useState({
        title: existingSeo?.title || '',
        description: existingSeo?.metadesc || '',
        focuskw: existingSeo?.focuskw || '',
        noindex: existingSeo?.noindex || false,
    });

    // Auto-generate formula title and description
    const getAutoTitle = () => {
        const brand = (formData.naziv_brenda || '').trim() || 'Novi Biznis';
        const catObj = categories.find(c => String(c.term_id) === String(formData.kategorija));
        const catName = catObj ? ` - ${catObj.name}` : '';
        const loc = (formData.lokacija || '').trim() ? ` ${formData.lokacija.trim()}` : '';
        return `${brand}${catName} | Prodaja Biznisa${loc} - CBB.hr`;
    };

    const getAutoDesc = () => {
        let summary = (formData.sazetak || '').trim();
        if (!summary && formData.o_poslovanju) {
            summary = formData.o_poslovanju.replace(/<[^>]*>/g, '').trim().slice(0, 150);
        }
        const metaParts = [];
        if ((formData.lokacija || '').trim()) metaParts.push(`Lokacija: ${formData.lokacija.trim()}`);
        if ((formData.cijena || '').trim()) metaParts.push(`Cijena: ${formData.cijena.trim()} €`);
        if ((formData.faza_prodaje || '').trim()) metaParts.push(`Status: ${formData.faza_prodaje.trim()}`);
        const extra = metaParts.length > 0 ? ` (${metaParts.join(' | ')}).` : '.';

        if (summary) {
            return `${summary.slice(0, 120)}...${extra}`;
        }
        const brand = (formData.naziv_brenda || '').trim() || 'poslovanje';
        return `Saznajte više o ponudi za ${brand}. Provjerite detalje i uvjete preuzimanja na CBB.hr${extra}`;
    };

    const handleToggleCustomSeo = (e) => {
        const enabled = e.target.checked;
        setCustomSeoEnabled(enabled);

        if (enabled) {
            // When toggled ON, if inputs are currently empty, pre-fill them using the formula!
            setSeoData(prev => ({
                ...prev,
                title: prev.title.trim() ? prev.title : getAutoTitle(),
                description: prev.description.trim() ? prev.description : getAutoDesc(),
            }));
        }
    };

    const handleResetToFormula = () => {
        setSeoData(prev => ({
            ...prev,
            title: getAutoTitle(),
            description: getAutoDesc(),
        }));
    };

    const handleChangeSeoData = (field, value) => {
        setSeoData(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const fetchKategorije = async () => {
            const token = localStorage.getItem('wp_jwt');
            if (!token) return;
            const apiUrl = process.env.REACT_APP_WORDPRESS_URL;
            const res = await fetch(`${apiUrl}/wp-json/biznis/v1/kategorije`,{
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await res.json();
            setCategories(data);
        };

        fetchKategorije();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleToggle = (name) => (e) => {
        setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
    };

    const handleImageChange = (e) => {
        setFormData((prev) => ({ ...prev, featured_image: e.target.files[0] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        const token = localStorage.getItem('wp_jwt');
        const apiUrl = process.env.REACT_APP_WORDPRESS_URL;

        const data = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                data.append(key, value);
            }
        });
        if (business && business.id) {
            data.append('id', business.id);
        }
        data.append('status', formData.draft ? 'draft' : 'publish');

        // Yoast SEO fields
        if (customSeoEnabled) {
            data.append('seo_title', seoData.title);
            data.append('seo_description', seoData.description);
            data.append('seo_focuskw', seoData.focuskw);
            data.append('seo_noindex', seoData.noindex ? '1' : '0');
        } else if (business && business.id) {
            // Revert back to automatic formula
            data.append('seo_title', '');
            data.append('seo_description', '');
            data.append('seo_focuskw', '');
        }


        try {
            const res = await fetch(`${apiUrl}/wp-json/biznis/v1/biznisi`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: data,
            });
            await res.json();
            setProcessing(false);
            Swal.fire({
                position: "top-end",
                icon: "success",
                html: `Biznis ${business ? 'updated' : 'created'} successfully.`,
                showConfirmButton: false,
                timer: 1500
            }).then(() => {
                onCancel(true);
            });
        } catch (err) {
            setProcessing(false);

            Swal.fire({
                html: 'Error creating Biznis',
                icon: 'error',
                showConfirmButton: false,
                timer: 5000,
                position: "bottom-end",
            });
            console.error('Error creating biznis:', err);
        }
    };

    const handleDelete = async () => {
        if (!business || !business.id) return;

        const confirm = await Swal.fire({
            title: 'Jeste li sigurni?',
            text: 'Ovo će trajno izbrisati Biznis oglas.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Da, izbriši!',
            cancelButtonText: 'Otkaži',
            confirmButtonColor: 'red',
        });


        if (!confirm.isConfirmed) return;

        const token = localStorage.getItem('wp_jwt');
        const apiUrl = process.env.REACT_APP_WORDPRESS_URL;

        try {
            setProcessingDelete(true)
            const res = await fetch(`${apiUrl}/wp-json/biznis/v1/biznisi/${business.id}?force=true`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await res.json();

            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Biznis has been deleted successfully.',
                    timer: 1500,
                    showConfirmButton: false,
                    position: "top-end",
                }).then(() => {
                    setProcessingDelete(false)
                    onCancel(true); // or refetch list, navigate, etc.
                });
            } else {
                setProcessingDelete(false)
                throw new Error(result.message || 'Failed to delete biznis');
            }
        } catch (err) {
            setProcessingDelete(false)
            console.error('Error deleting biznis:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || 'Something went wrong while deleting.',
                position: "bottom-end",
            });
        }
    };




    return (
        <div>
            <form className="space-y-4" onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="flex flex-col xl:flex-row justify-start items-start">
                    <div
                        className="flex flex-row-reverse md:flex-row  justify-between md:justify-start border-gray-200 shadow rounded-lg p-4 w-full">
                        <div className="flex flex-col items-start">
                            <InputLabel value="Istaknuta Slika"/>
                            <div
                                className="w-40 h-40 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-indigo-500"
                                style={{height: '220px', width: '200px'}}
                                onClick={() => document.getElementById('featured_image_input').click()}
                            >
                                {formData.featured_image ? (
                                    <img
                                        src={URL.createObjectURL(formData.featured_image)}
                                        alt="Preview"
                                        className="w-full h-full object-cover rounded"
                                        style={{height: '100%', width: '100%', objectFit: 'cover'}}
                                    />
                                ) : (business && business.featured_image ? (
                                        <img
                                            src={business.featured_image}
                                            alt="Preview"
                                            className="w-full h-full object-cover rounded"
                                            style={{height: '100%', width: '100%', objectFit: 'cover'}}
                                        />
                                    ) : (
                                        <span className="text-gray-400 text-sm flex items-center">Klikni za sliku</span>
                                    )
                                )}
                            </div>
                            <input
                                id="featured_image_input"
                                type="file"
                                name="featured_image"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageChange}
                            />
                        </div>
                        <div className="ml-0 md:ml-4 mr-4 mt-4 sm:mt-0 md:mr-0 flex flex-col">
                            <div className={'mb-4'}>
                                <InputLabel value={'Naziv brenda*'}/>
                                <TextInput value={formData.naziv_brenda} type="text" placeholder="" name="naziv_brenda"
                                           onChange={handleChange} required/>
                            </div>
                            <div className={'mb-4'}>
                                <InputLabel value={'Sažetak*'}/>
                                <TextInput value={formData.sazetak} type="text" placeholder="" name="sazetak"
                                           onChange={handleChange} required/>
                            </div>
                            <div className={'mb-4'}>
                                <InputLabel value={'Lokacija*'}/>
                                <TextInput value={formData.lokacija} type="text" placeholder="" name="lokacija"
                                           onChange={handleChange} required/>
                            </div>
                        </div>
                    </div>
                    <div className="ml-0 lg:ml-4 mt-4 lg:mt-0 flex flex-col md:flex-row w-full">
                        <div className='flex sm:flex-row justify-between border-gray-200 shadow rounded-lg p-4 w-full'>
                            <div className="ml-0 xl:ml-4 flex flex-col w-1/2">
                                <div className={'mb-4'}>
                                    <InputLabel value={'Cijena*'}/>
                                    <TextInput value={formData.cijena} type="text" placeholder="" name="cijena"
                                               onChange={handleChange} required/>
                                </div>
                                <div className={'mb-4'}>
                                    <InputLabel value={'Prihod'}/>
                                    <TextInput value={formData.prihod} type="text" placeholder="" name="prihod"
                                               onChange={handleChange}/>
                                </div>
                                <div className={'mb-4'}>
                                    <InputLabel value={'SDE'}/>
                                    <TextInput value={formData.sde} type="text" placeholder="" name="sde"
                                               onChange={handleChange}/>
                                </div>
                            </div>
                            <div className="ml-4 flex flex-col w-1/2">
                                <div className={'mb-4'}>
                                    <InputLabel value={'ID BROJ*'}/>
                                    <TextInput value={formData.listing_id} type="text" placeholder="" name="listing_id"
                                               onChange={handleChange} required/>
                                </div>
                                <div className={'mb-4'}>
                                    <InputLabel value={'Udio na prodaju (%)'}/>
                                    <TextInput value={formData.udio_na_prodaju} type="text" placeholder=""
                                               name="udio_na_prodaju" onChange={handleChange}/>
                                </div>
                                <div className={'mb-4'}>
                                    <InputLabel value={'Kategoriju*'}/>
                                    <select value={formData.kategorija} required
                                            className={'p-3 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-md'}
                                            name="kategorija" onChange={handleChange}>
                                        <option value="" disabled>Odaberi kategoriju</option>
                                        {categories.map(cat => (
                                            <option key={cat.term_id} value={cat.term_id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                            </div>
                        </div>

                        <div
                            className="ml-0 md:ml-4 mt-4 md:mt-0 flex md:flex-col border items-center md:items-start justify-between md:justify-start border-gray-200 shadow rounded-lg p-4 w-full">
                            <div className="">
                                <InputLabel value={'Faza Prodaje*'}/>
                                <select value={formData.faza_prodaje}
                                        className={'p-3 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-md'}
                                        name="faza_prodaje" required onChange={handleChange}>
                                    <option value="" disabled>Odaberi fazu prodaje</option>
                                    <option value="Na prodaju">Na prodaju</option>
                                    <option value="U pregovorima">U pregovorima</option>
                                    <option value="Prodano">Prodano</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-4 mt-4 md:mt-6">
                               
                                <Toggle
                                    id="ekskluzivna_ponuda"
                                    checked={formData.ekskluzivna_ponuda}
                                    onChange={handleToggle('ekskluzivna_ponuda')}
                                    label="Ekskluzivna ponuda"
                                />
                                <Toggle
                                    id="istaknuta_ponuda"
                                    checked={formData.istaknuta_ponuda}
                                    onChange={handleToggle('istaknuta_ponuda')}
                                    label="Istaknuta ponuda"
                                />
                                <Toggle
                                    id="delisted"
                                    checked={formData.delisted}
                                    onChange={handleToggle('delisted')}
                                    label="Delistiran"
                                />
                                <Toggle
                                    id="draft"
                                    checked={formData.draft}
                                    onChange={handleToggle('draft')}
                                    label="Nacrt"
                                />
                            </div>
                        </div>
                    </div>

                </div>
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-full md:col-span-7">
                        <div>
                            <InputLabel value="O poslovanju"/>
                            <Editor
                                value={formData.o_poslovanju}
                                licenseKey={'gpl'}
                                tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`} // 👈 important!
                                init={{
                                    height: 420,
                                    width: '100%',
                                    menubar: false,
                                    skin_url: `${process.env.PUBLIC_URL}/tinymce/skins/ui/oxide`, // 👈 point to local skin
                                    content_css: `${process.env.PUBLIC_URL}/tinymce/skins/content/default/content.css`, // 👈 point to local content css
                                    toolbar: 'bold italic backcolor | undo redo | formatselect | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
                                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                }}
                                onEditorChange={(content) => {
                                    setFormData(prev => ({...prev, o_poslovanju: content}));
                                }}
                            />
                        </div>

                    </div>
                    <div className="col-span-full md:col-span-5">
                        <div>
                            <InputLabel value="Bilješke administratora:"/>
                            <Editor
                                value={formData.notes}
                                licenseKey={'gpl'}
                                tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`} // 👈 important!
                                init={{
                                    height: 350,
                                    width: '100%',
                                    menubar: false,
                                    skin_url: `${process.env.PUBLIC_URL}/tinymce/skins/ui/oxide`, // 👈 point to local skin
                                    content_css: `${process.env.PUBLIC_URL}/tinymce/skins/content/default/content.css`, // 👈 point to local content css
                                    toolbar: 'bold italic backcolor | undo redo | formatselect | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
                                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                }}
                                onEditorChange={(content) => {
                                    setFormData(prev => ({...prev, notes: content}));
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Yoast SEO Optimization Bar with Live Rules */}
                <div className="mt-4">
                    <SeoSection
                        formData={formData}
                        business={business}
                        featuredImageUrl={
                            formData.featured_image
                                ? URL.createObjectURL(formData.featured_image)
                                : (business?.featured_image || null)
                        }
                        categories={categories}
                        customSeoEnabled={customSeoEnabled}
                        onToggleCustomSeo={handleToggleCustomSeo}
                        seoData={seoData}
                        onChangeSeoData={handleChangeSeoData}
                        onResetToFormula={handleResetToFormula}
                        computedAutoTitle={getAutoTitle()}
                        computedAutoDesc={getAutoDesc()}
                    />
                </div>

                {/* Bottom Action Bar */}
                <div className="flex flex-row mt-6 w-full justify-end items-center">
                    {business && business.id && (
                        <SecondaryButton
                            type="button"
                            onClick={handleDelete}
                            className="mr-3 text-red hover:bg-red-50"
                            style={{ color: 'red' }}
                            disabled={processingDelete}
                        >
                            {processingDelete ? (
                                <div className="animate-spin border-2 border-t-2 border-blue-950 w-5 h-5 rounded-full"></div>
                            ) : (
                                'Izbrisati'
                            )}
                        </SecondaryButton>
                    )}
                    <div>
                        <PrimaryButton
                            type="submit"
                            className="px-6 py-2.5 rounded shadow-sm text-sm font-semibold"
                            disabled={processing || processingDraft}
                        >
                            {processing ? (
                                <div className="animate-spin border-2 border-t-2 border-white w-5 h-5 rounded-full"></div>
                            ) : (
                                'Spremi Biznis'
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            </form>
        </div>
    );
}
